import React, { useState, Component } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Phone, Headset, FileText, Scale, Users, Gavel, ShieldCheck, ArrowRight, CheckCircle, Monitor, FileCheck, AlertCircle, Download, ChevronDown, Activity, Lock, Globe, Workflow, ClipboardList, LifeBuoy, Handshake, LogIn, UserCheck, Send } from 'lucide-react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
type StepType = 'online' | 'phone' | 'lawyer' | 'optional' | 'end';
interface SwimStep {
  title: string;
  type: StepType;
  desc?: string;
  col: number; // 1..10
}
interface SwimLane {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  steps: SwimStep[];
}
const stepStyles: Record<StepType, {
  border: string;
  chipBg: string;
  chipText: string;
  chipLabel: string;
}> = {
  online: {
    border: 'border-l-violetBlue',
    chipBg: 'bg-violetBlue/10',
    chipText: 'text-violetBlue',
    chipLabel: 'Online'
  },
  phone: {
    border: 'border-l-turquoise',
    chipBg: 'bg-turquoise/10',
    chipText: 'text-turquoise',
    chipLabel: 'Phone'
  },
  lawyer: {
    border: 'border-l-primary',
    chipBg: 'bg-primary/10',
    chipText: 'text-primary',
    chipLabel: 'Lawyer'
  },
  optional: {
    border: 'border-l-disabled border-dashed',
    chipBg: 'bg-aliceBlue',
    chipText: 'text-secondary',
    chipLabel: 'Optional'
  },
  end: {
    border: 'border-l-turquoise',
    chipBg: 'bg-turquoise/10',
    chipText: 'text-turquoise',
    chipLabel: 'End'
  }
};
export function PartnerWorkflow() {
  const navigate = useNavigate();
  const ctx = useOutletContext<DashboardContext>();
  const toggleMobileSidebar = ctx?.toggleMobileSidebar ?? (() => {});
  const [expandedEdgeCase, setExpandedEdgeCase] = useState<number | null>(0);
  const overviewCards = [{
    step: '01',
    icon: LogIn,
    title: 'User Access',
    desc: 'TELUS / Assistenza user is verified and granted access to LawVo.'
  }, {
    step: '02',
    icon: ClipboardList,
    title: 'Legal Triage',
    desc: 'LawVo identifies the legal matter, dispute amount, litigation risk, and recommended path.'
  }, {
    step: '03',
    icon: Phone,
    title: 'Lawyer / Phone Support',
    desc: 'Telephone legal assistance provides legal information and may create a file.'
  }, {
    step: '04',
    icon: Monitor,
    title: 'Digital Next Steps',
    desc: 'Access documents, mediation setup, small claims guidance, or lawyer review.'
  }];
  const swimlanes: SwimLane[] = [{
    label: 'User',
    icon: Users,
    steps: [{
      title: 'User logs in',
      type: 'online',
      col: 1
    }, {
      title: 'Identifies legal need',
      type: 'online',
      col: 3
    }, {
      title: 'Chooses path: phone, online, or hybrid',
      type: 'online',
      col: 4
    }, {
      title: 'Completes mediation intake',
      type: 'optional',
      col: 7
    }, {
      title: 'Receives outcome',
      type: 'end',
      col: 10
    }]
  }, {
    label: 'TELUS / Assistenza',
    icon: ShieldCheck,
    steps: [{
      title: 'Verifies eligibility',
      type: 'online',
      col: 2
    }, {
      title: 'Agent opt-out fallback',
      type: 'optional',
      col: 5
    }, {
      title: 'Agent support routing',
      type: 'phone',
      col: 9
    }]
  }, {
    label: 'Telephone Legal Assistance / Lawyer',
    icon: Headset,
    steps: [{
      title: 'Provides telephone legal info',
      type: 'phone',
      col: 4
    }, {
      title: 'Guides small claims setup',
      type: 'phone',
      col: 5
    }, {
      title: 'Creates case / file',
      type: 'lawyer',
      col: 6
    }, {
      title: 'Triggers document access',
      type: 'lawyer',
      col: 7
    }, {
      title: 'Optional lawyer review',
      type: 'optional',
      col: 9
    }]
  }, {
    label: 'LawVo Platform',
    icon: Workflow,
    steps: [{
      title: 'Grants platform access',
      type: 'online',
      col: 2
    }, {
      title: 'Runs triage questionnaire',
      type: 'online',
      col: 3
    }, {
      title: 'Builds structured case file',
      type: 'online',
      col: 6
    }, {
      title: 'Sends mediation setup link',
      type: 'online',
      col: 7
    }, {
      title: 'Small claims guidance',
      type: 'online',
      col: 8
    }, {
      title: 'End of file summary',
      type: 'end',
      col: 10
    }]
  }];
  const journeyRows = [{
    stage: 'Access',
    user: 'Signs in with partner credentials.',
    system: 'Validates session and partner eligibility.',
    support: 'No action needed.',
    outcome: 'User reaches the LawVo dashboard.',
    dot: 'bg-violetBlue'
  }, {
    stage: 'Intake',
    user: 'Describes the legal need.',
    system: 'Captures matter type and routes path.',
    support: 'Optional: phone agent answers initial questions.',
    outcome: 'Path selected (online, phone, or hybrid).',
    dot: 'bg-violetBlue'
  }, {
    stage: 'Triage',
    user: 'Answers six guided questions.',
    system: 'Recommends mediation, documents, or lawyer review.',
    support: 'Lawyer can review answers asynchronously.',
    outcome: 'Personalized next steps generated.',
    dot: 'bg-primary'
  }, {
    stage: 'Phone Support',
    user: 'Speaks with a legal expert by phone.',
    system: 'Logs call summary to the file.',
    support: 'Lawyer gives legal information and guidance.',
    outcome: 'User understands their options.',
    dot: 'bg-turquoise'
  }, {
    stage: 'File Creation',
    user: 'Confirms personal and matter details.',
    system: 'Builds a structured case file.',
    support: 'Lawyer attaches notes and recommended next step.',
    outcome: 'Single source of truth for the matter.',
    dot: 'bg-primary'
  }, {
    stage: 'Documents',
    user: 'Opens or completes interactive documents.',
    system: 'Tracks access mode (benefit, pay-per-use, triggered).',
    support: 'Lawyer can unlock or recommend documents.',
    outcome: 'Documents drafted and stored securely.',
    dot: 'bg-violetBlue'
  }, {
    stage: 'Mediation',
    user: 'Completes mediation intake form.',
    system: 'Sends setup link and tracks progress.',
    support: 'Lawyer recommends mediation when appropriate.',
    outcome: 'Mediation initiated with both parties.',
    dot: 'bg-violetBlue'
  }, {
    stage: 'Small Claims',
    user: 'Confirms dispute amount and prepares documents.',
    system: 'Provides step-by-step filing instructions.',
    support: 'Lawyer review optional before filing.',
    outcome: 'User is ready to file or proceed.',
    dot: 'bg-turquoise'
  }, {
    stage: 'Agent Escalation',
    user: 'Requests to speak with an agent.',
    system: 'Pauses digital flow and routes to phone.',
    support: 'Assistenza agent picks up the request.',
    outcome: 'User receives live human support.',
    dot: 'bg-destructive'
  }, {
    stage: 'File Closure',
    user: 'Reviews summary of help received.',
    system: 'Closes the file and archives records.',
    support: 'Lawyer signs off as needed.',
    outcome: 'File closed with clear outcome recorded.',
    dot: 'bg-turquoise'
  }];
  const recommendedScreens: {
    icon: ComponentType<{
      className?: string;
    }>;
    title: string;
    desc: string;
    tag: 'Required' | 'Partner Flow' | 'Lawyer Triggered' | 'Optional' | 'Fallback';
  }[] = [{
    icon: Lock,
    title: 'Login / Partner Access Screen',
    desc: 'Branded sign-in for TELUS and Assistenza users with eligibility check.',
    tag: 'Required'
  }, {
    icon: ClipboardList,
    title: 'Legal Need Intake Screen',
    desc: "Plain-language form to capture the user's legal situation.",
    tag: 'Required'
  }, {
    icon: Activity,
    title: 'Triage Questionnaire',
    desc: 'Six guided questions that recommend a clear next step.',
    tag: 'Required'
  }, {
    icon: FileCheck,
    title: 'Case / File Summary',
    desc: 'Structured file with user details, notes, and recommended path.',
    tag: 'Lawyer Triggered'
  }, {
    icon: FileText,
    title: 'Interactive Document Access',
    desc: 'Documents available as a benefit, pay-per-use, or unlocked by a lawyer.',
    tag: 'Partner Flow'
  }, {
    icon: Handshake,
    title: 'Mediation Setup Link',
    desc: 'One-click link that walks users into the mediation intake.',
    tag: 'Lawyer Triggered'
  }, {
    icon: Gavel,
    title: 'Small Claims Guidance',
    desc: 'Step-by-step instructions with optional lawyer review.',
    tag: 'Optional'
  }, {
    icon: LifeBuoy,
    title: 'Agent Opt-Out',
    desc: 'Visible fallback to reach a human agent at any point.',
    tag: 'Fallback'
  }, {
    icon: CheckCircle,
    title: 'End of File Summary',
    desc: 'Clear recap of outcome, next actions, and stored documents.',
    tag: 'Required'
  }];
  const tagStyles: Record<string, string> = {
    Required: 'bg-violetBlue/10 text-violetBlue',
    'Partner Flow': 'bg-primary/10 text-primary',
    'Lawyer Triggered': 'bg-turquoise/10 text-turquoise',
    Optional: 'bg-aliceBlue text-secondary border border-borderColor',
    Fallback: 'bg-destructive/10 text-destructive'
  };
  const edgeCases = [{
    title: 'User does not qualify for access',
    desc: 'If eligibility fails, the user sees a clear message explaining why and is offered TELUS or Assistenza contact options. No file is created.'
  }, {
    title: 'User wants phone support only',
    desc: 'The flow routes directly to telephone legal assistance. The platform records minimal metadata so the lawyer has context when they call.'
  }, {
    title: 'User needs legal representation',
    desc: 'Triage flags representation. The lawyer can convert the file into a referral and connect the user to a representing lawyer.'
  }, {
    title: 'User needs documents only',
    desc: 'Skip triage and route the user to the document library. Access mode is set based on the partner benefit and pay-per-use rules.'
  }, {
    title: 'User starts online but switches to phone',
    desc: 'The platform saves progress and hands off context to the phone agent. The lawyer continues from where the user left off.'
  }, {
    title: 'Lawyer creates file after phone call',
    desc: 'Lawyer opens a new case from their dashboard, attaches call notes, and invites the user to the LawVo platform with a one-time link.'
  }, {
    title: 'User abandons workflow',
    desc: 'Auto-save preserves progress for 30 days. A reminder is sent and the file is marked "pending" instead of closed.'
  }, {
    title: 'Mediation is not appropriate',
    desc: 'Triage hides the mediation path. The user is offered small claims guidance or lawyer review instead.'
  }, {
    title: 'Small claims amount is outside eligible range',
    desc: 'The platform explains provincial limits and recommends alternatives such as mediation or formal legal action.'
  }, {
    title: 'User requests agent support',
    desc: 'The opt-out is always visible. Clicking it pauses the digital flow and routes the user to a TELUS or Assistenza agent.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-3909">
      <DashboardHeader categories={[]} onMenuClick={toggleMobileSidebar} onNavigate={path => navigate(path)} data-id="element-3910" />

      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8" data-id="element-3911">
        {/* 1. Header Section */}
        <section className="bg-white border border-borderColor rounded-xl p-6 lg:p-8 shadow-sm" data-id="element-3912">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-3913">
            <span className="px-3 py-1 bg-violetBlue/10 text-violetBlue text-xs font-semibold rounded-full" data-id="element-3914">
              Partner Workflow
            </span>
            <span className="px-3 py-1 bg-turquoise/10 text-turquoise text-xs font-semibold rounded-full" data-id="element-3915">
              30% Online / 70% Telephone Support
            </span>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-primary mb-3" data-id="element-3916">
            LawVo + TELUS + Assistenza Workflow
          </h1>
          <p className="text-sm lg:text-base text-secondary max-w-3xl leading-relaxed" data-id="element-3917">
            Hybrid legal support journey combining telephone legal assistance,
            LawVo platform access, interactive documents, mediation, and small
            claims guidance.
          </p>
          <div className="mt-5 flex items-center gap-2 text-xs text-secondary" data-id="element-3918">
            <span className="font-semibold text-primary" data-id="element-3919">LawVo</span>
            <span className="text-disabled" data-id="element-3920">-</span>
            <span className="font-semibold text-primary" data-id="element-3921">TELUS</span>
            <span className="text-disabled" data-id="element-3922">-</span>
            <span className="font-semibold text-primary" data-id="element-3923">Assistenza</span>
          </div>
        </section>

        {/* 2. Workflow Overview Cards */}
        <section data-id="element-3924">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3925">
            {overviewCards.map(card => {
            const Icon = card.icon;
            return <div key={card.step} className="bg-white border border-borderColor rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-3926">
                  <div className="flex items-start justify-between mb-3" data-id="element-3927">
                    <div className="p-2 bg-aliceBlue rounded-lg" data-id="element-3928">
                      <Icon className="w-5 h-5 text-violetBlue" data-id="element-3929" />
                    </div>
                    <span className="text-xs font-bold text-violetBlue" data-id="element-3930">
                      {card.step}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-primary mb-1" data-id="element-3931">
                    {card.title}
                  </h3>
                  <p className="text-sm text-secondary leading-relaxed" data-id="element-3932">
                    {card.desc}
                  </p>
                </div>;
          })}
          </div>
        </section>

        {/* 3. Main Visual Workflow Diagram (Swimlanes) */}
        <section className="bg-white border border-borderColor rounded-xl p-5 lg:p-7 shadow-sm" data-id="element-3933">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5" data-id="element-3934">
            <div className="flex items-center gap-3" data-id="element-3935">
              <div className="p-2 bg-aliceBlue rounded-lg" data-id="element-3936">
                <Workflow className="w-5 h-5 text-violetBlue" data-id="element-3937" />
              </div>
              <div data-id="element-3938">
                <h2 className="text-lg font-semibold text-primary" data-id="element-3939">
                  End-to-End Workflow
                </h2>
                <p className="text-sm text-secondary" data-id="element-3940">
                  Swimlane view of how each partner contributes to the user
                  journey.
                </p>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-2" data-id="element-3941">
              {(['online', 'phone', 'lawyer', 'optional', 'end'] as StepType[]).map(t => <span key={t} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${stepStyles[t].chipBg} ${stepStyles[t].chipText}`} data-id="element-3942">
                  <span className={`w-1.5 h-1.5 rounded-full ${t === 'online' ? 'bg-violetBlue' : t === 'phone' || t === 'end' ? 'bg-turquoise' : t === 'lawyer' ? 'bg-primary' : 'bg-disabled'}`} data-id="element-3943" />
                  {stepStyles[t].chipLabel}
                </span>)}
            </div>
          </div>

          {/* Stage headers + lanes */}
          <div className="overflow-x-auto -mx-2 px-2" data-id="element-3944">
            <div className="min-w-[1200px]" data-id="element-3945">
              {/* Stage header row */}
              <div className="flex items-stretch mb-2" data-id="element-3946">
                <div className="w-44 flex-shrink-0" data-id="element-3947" />
                <div className="flex-1 grid grid-cols-10 gap-2 px-2" data-id="element-3948">
                  {['A. Access', 'A. Eligibility', 'B. Intake', 'B/C. Path', 'C. Phone', 'E. File', 'F/G. Docs & Mediation', 'H. Small Claims', 'I. Escalation', 'J. End of File'].map(label => <div key={label} className="text-[10px] font-semibold uppercase tracking-wide text-secondary text-center py-1" data-id="element-3949">
                      {label}
                    </div>)}
                </div>
              </div>

              {/* Swimlanes */}
              <div className="space-y-3" data-id="element-3950">
                {swimlanes.map(lane => {
                const LaneIcon = lane.icon;
                return <div key={lane.label} className="flex items-stretch bg-aliceBlue/40 rounded-xl border border-borderColor" data-id="element-3951">
                      {/* Lane label */}
                      <div className="w-44 flex-shrink-0 flex items-center gap-2 px-4 py-4 bg-aliceBlue rounded-l-xl border-r border-borderColor" data-id="element-3952">
                        <LaneIcon className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-3953" />
                        <span className="text-sm font-semibold text-primary leading-tight" data-id="element-3954">
                          {lane.label}
                        </span>
                      </div>

                      {/* Lane content */}
                      <div className="flex-1 grid grid-cols-10 gap-2 p-3 relative" data-id="element-3955">
                        {lane.steps.map((step, i) => {
                      const s = stepStyles[step.type];
                      return <div key={`${lane.label}-${i}`} className={`bg-white border border-borderColor border-l-4 ${s.border} rounded-lg p-2.5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-1.5`} style={{
                        gridColumnStart: step.col,
                        gridColumnEnd: `span 1`
                      }} data-id="element-3956">
                              <span className={`inline-flex items-center self-start px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${s.chipBg} ${s.chipText}`} data-id="element-3957">
                                {s.chipLabel}
                              </span>
                              <p className="text-xs font-medium text-primary leading-snug" data-id="element-3958">
                                {step.title}
                              </p>
                            </div>;
                    })}
                      </div>
                    </div>;
              })}
              </div>

              {/* Flow arrow row */}
              <div className="flex items-center mt-4" data-id="element-3959">
                <div className="w-44 flex-shrink-0" data-id="element-3960" />
                <div className="flex-1 flex items-center justify-between px-2 text-disabled" data-id="element-3961">
                  <ArrowRight className="w-4 h-4" data-id="element-3962" />
                  <ArrowRight className="w-4 h-4" data-id="element-3963" />
                  <ArrowRight className="w-4 h-4" data-id="element-3964" />
                  <ArrowRight className="w-4 h-4" data-id="element-3965" />
                  <ArrowRight className="w-4 h-4" data-id="element-3966" />
                  <ArrowRight className="w-4 h-4" data-id="element-3967" />
                  <ArrowRight className="w-4 h-4" data-id="element-3968" />
                  <ArrowRight className="w-4 h-4" data-id="element-3969" />
                  <ArrowRight className="w-4 h-4" data-id="element-3970" />
                </div>
              </div>
            </div>
          </div>

          {/* Triage card highlight */}
          <div className="mt-6 bg-aliceBlue border border-borderColor rounded-xl p-5" data-id="element-3971">
            <div className="flex items-center gap-2 mb-3" data-id="element-3972">
              <Scale className="w-4 h-4 text-violetBlue" data-id="element-3973" />
              <h3 className="text-sm font-semibold text-primary" data-id="element-3974">
                LawVo Triage Questions
              </h3>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-secondary" data-id="element-3975">
              {['What is the legal matter?', 'What is the amount of the legal dispute?', 'Is litigation involved?', 'Is mediation appropriate?', 'Are interactive legal documents needed?', 'Is lawyer review or legal representation required?'].map(q => <li key={q} className="flex items-start gap-2" data-id="element-3976">
                  <CheckCircle className="w-4 h-4 text-turquoise mt-0.5 flex-shrink-0" data-id="element-3977" />
                  <span data-id="element-3978">{q}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* 4. Journey Map Table */}
        <section className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-3979">
          <div className="px-6 py-5 border-b border-borderColor flex items-center gap-3" data-id="element-3980">
            <div className="p-2 bg-aliceBlue rounded-lg" data-id="element-3981">
              <ClipboardList className="w-5 h-5 text-violetBlue" data-id="element-3982" />
            </div>
            <div data-id="element-3983">
              <h2 className="text-lg font-semibold text-primary" data-id="element-3984">
                Journey Map
              </h2>
              <p className="text-sm text-secondary" data-id="element-3985">
                Who does what at each stage of the partner journey.
              </p>
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3986">
            <table className="min-w-full text-left" data-id="element-3987">
              <thead data-id="element-3988">
                <tr data-id="element-3989">
                  {['Stage', 'User Action', 'System Action', 'Support / Lawyer Action', 'Outcome'].map(h => <th key={h} className="text-xs font-semibold uppercase tracking-wide text-secondary bg-aliceBlue px-4 py-3 whitespace-nowrap" data-id="element-3990">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-3991">
                {journeyRows.map(row => <tr key={row.stage} className="hover:bg-aliceBlue/50 transition-colors" data-id="element-3992">
                    <td className="px-4 py-3 text-sm text-primary border-t border-borderColor whitespace-nowrap" data-id="element-3993">
                      <span className="inline-flex items-center gap-2" data-id="element-3994">
                        <span className={`w-2 h-2 rounded-full ${row.dot}`} data-id="element-3995" />
                        <span className="font-semibold" data-id="element-3996">{row.stage}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary border-t border-borderColor" data-id="element-3997">
                      {row.user}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary border-t border-borderColor" data-id="element-3998">
                      {row.system}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary border-t border-borderColor" data-id="element-3999">
                      {row.support}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary border-t border-borderColor" data-id="element-4000">
                      {row.outcome}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>

        {/* 5. Recommended Product Screens */}
        <section data-id="element-4001">
          <div className="flex items-center gap-3 mb-5" data-id="element-4002">
            <div className="p-2 bg-aliceBlue rounded-lg" data-id="element-4003">
              <Monitor className="w-5 h-5 text-violetBlue" data-id="element-4004" />
            </div>
            <div data-id="element-4005">
              <h2 className="text-lg font-semibold text-primary" data-id="element-4006">
                Recommended Screens / UX Touchpoints
              </h2>
              <p className="text-sm text-secondary" data-id="element-4007">
                Screens needed to deliver this workflow end to end.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4008">
            {recommendedScreens.map(s => {
            const Icon = s.icon;
            return <div key={s.title} className="bg-white border border-borderColor rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-4009">
                  <div className="p-2 bg-aliceBlue rounded-lg w-fit mb-3" data-id="element-4010">
                    <Icon className="w-5 h-5 text-violetBlue" data-id="element-4011" />
                  </div>
                  <h3 className="text-base font-semibold text-primary mb-1" data-id="element-4012">
                    {s.title}
                  </h3>
                  <p className="text-sm text-secondary leading-relaxed flex-1" data-id="element-4013">
                    {s.desc}
                  </p>
                  <span className={`mt-4 inline-flex items-center self-start px-2.5 py-1 rounded-full text-xs font-medium ${tagStyles[s.tag]}`} data-id="element-4014">
                    {s.tag}
                  </span>
                </div>;
          })}
          </div>
        </section>

        {/* 6. Routing Logic & Edge Cases */}
        <section className="bg-white border border-borderColor rounded-xl p-5 lg:p-7 shadow-sm" data-id="element-4015">
          <div className="flex items-center gap-3 mb-5" data-id="element-4016">
            <div className="p-2 bg-aliceBlue rounded-lg" data-id="element-4017">
              <AlertCircle className="w-5 h-5 text-violetBlue" data-id="element-4018" />
            </div>
            <div data-id="element-4019">
              <h2 className="text-lg font-semibold text-primary" data-id="element-4020">
                Routing Logic & Edge Cases
              </h2>
              <p className="text-sm text-secondary" data-id="element-4021">
                How the workflow handles non-standard situations.
              </p>
            </div>
          </div>

          <div className="space-y-2" data-id="element-4022">
            {edgeCases.map((ec, idx) => {
            const open = expandedEdgeCase === idx;
            return <div key={ec.title} className="border border-borderColor rounded-lg overflow-hidden" data-id="element-4023">
                  <button type="button" onClick={() => setExpandedEdgeCase(open ? null : idx)} aria-expanded={open} className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${open ? 'bg-aliceBlue' : 'bg-white hover:bg-aliceBlue/60'}`} data-id="element-4024">
                    <span className="text-sm font-medium text-primary" data-id="element-4025">
                      {ec.title}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-secondary transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-4026" />
                  </button>
                  {open && <div className="px-4 pb-4 pt-1 text-sm text-secondary leading-relaxed bg-aliceBlue/50" data-id="element-4027">
                      {ec.desc}
                    </div>}
                </div>;
          })}
          </div>
        </section>

        {/* 7. CTA / Footer Area */}
        <section className="bg-white border border-borderColor rounded-xl p-6 lg:p-7 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-4028">
          <div data-id="element-4029">
            <h3 className="text-lg font-semibold text-primary mb-1" data-id="element-4030">
              Ready to align with partners?
            </h3>
            <p className="text-sm text-secondary" data-id="element-4031">
              Share this workflow with LawVo, TELUS, Assistenza, product,
              design, and development stakeholders.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-4032">
            <button type="button" className="bg-violetBlue hover:bg-[#6844E8] text-white font-medium py-2.5 px-5 rounded-lg transition-colors inline-flex items-center justify-center gap-2" data-id="element-4033">
              Review Workflow
              <ArrowRight className="w-4 h-4" data-id="element-4034" />
            </button>
            <button type="button" className="bg-white border-2 border-violetBlue text-violetBlue hover:bg-aliceBlue font-medium py-2.5 px-5 rounded-lg transition-colors inline-flex items-center justify-center gap-2" data-id="element-4035">
              <Download className="w-4 h-4" data-id="element-4036" />
              Export Journey Map
            </button>
            <button type="button" className="text-violetBlue hover:underline font-medium text-sm inline-flex items-center justify-center gap-1.5" data-id="element-4037">
              <Send className="w-4 h-4" data-id="element-4038" />
              Send to Partner Team
            </button>
          </div>
        </section>
      </main>
    </div>;
}